import { useMemo } from "react";
import moment from "moment";
import type { tAnyTask, tMomentComplatibleDate } from "@/types";

type tArgs = {
  task: tAnyTask;
  now: tMomentComplatibleDate;
  isTaskMaster: boolean;
  isTaskConsumer: boolean;
};
type tSelectorArgs = tArgs & {
  isEnded: boolean;
  isStarted: boolean;
};
type tSelectorFn = (args: tSelectorArgs) => boolean;

const cancelSelector: tSelectorFn = ({
  task,
  now,
  isTaskConsumer,
  isEnded,
  isStarted,
}) => {
  const { status } = task;
  if (
    status === "CANCELLED" ||
    status === "DISPUTE" ||
    status === "DISPUTE_REJECTED" ||
    status === "DISPUTE_APPROVED" ||
    status === "DONE"
  ) {
    return false;
  }

  if (task.personal) return true;

  if (status === "PAID" && isStarted) {
    if (!isTaskConsumer) {
      return moment(task.dispute_deadline).isBefore(now);
    } else {
      return isEnded;
    }
  }
  return true;
};
const acceptSelector: tSelectorFn = ({ isTaskConsumer, task }) => {
  return !isTaskConsumer && task.status === "PENDING";
};
const changeSelector: tSelectorFn = ({ task }) => {
  // if (!task.personal && task.safe) {
  //   return task.status === "PENDING";
  // }
  return task.status !== "CANCELLED";
};
const reviewSelector: tSelectorFn = ({ task, isTaskConsumer }) => {
  if (task.custom) return false;
  if (!isTaskConsumer) return false;
  return task.status === "DONE";
};
const paySelector: tSelectorFn = ({ task, isTaskConsumer }) => {
  if (!isTaskConsumer) return false;
  return task.status === "PAY_WAITING";
};
const finishSelector: tSelectorFn = ({
  task,
  isTaskConsumer,
  isEnded,
  now,
}) => {
  if (task.status !== "PAID" && task.status !== "ACCEPTED") {
    return false;
  }
  if (task.custom) {
    return true;
  }
  if (!isTaskConsumer && task.safe) {
    return moment(task.dispute_deadline).isBefore(now);
  } else {
    return isEnded;
  }
};
const photoUploadSelector: tSelectorFn = ({ task }) => {
  return task.status === "DONE";
};
const photoRequestSelector: tSelectorFn = ({ task, isTaskConsumer }) => {
  if (task.status !== "DONE" || task.custom) {
    return false;
  }
  if (isTaskConsumer) {
    return !task.client_requested_photo;
  }
  return !task.master_requested_photo;
};
const reviewRequestSelector: tSelectorFn = ({ task, isTaskConsumer }) => {
  if (
    task.personal ||
    task.custom ||
    isTaskConsumer ||
    task.master_requested_review
  )
    return false;
  return task.status === "DONE";
};
const messageSelector: tSelectorFn = ({ task }) => {
  return Boolean(!task.personal && task.consumer);
};
const disputeSelector: tSelectorFn = ({
  task,
  isTaskConsumer,
  isEnded,
  now,
}) => {
  return (
    !task.personal &&
    isTaskConsumer &&
    task.status === "PAID" &&
    isEnded &&
    moment(task.dispute_deadline).isAfter(now)
  );
};
const disputeUpdateSelector: tSelectorFn = ({ task, isTaskConsumer }) => {
  return isTaskConsumer && task.status === "DISPUTE";
};
const disputeRemoveSelector: tSelectorFn = ({ task, isTaskConsumer }) => {
  return isTaskConsumer && task.status === "DISPUTE";
};
const removeSelector: tSelectorFn = ({ task }) => {
  if (task.personal) return true;
  return !task.safe;
};
const remindSelector: tSelectorFn = ({
  task,
  isTaskMaster,
  isStarted,
  isEnded,
}) => {
  if (task.personal || !isTaskMaster || !task.client) return false;
  if (isStarted || isEnded) return false;
  return task.status === "ACCEPTED" || task.status === "PAID";
};
const copyTaskSelector: tSelectorFn = ({ isTaskConsumer }) => !isTaskConsumer;

const createWarehouseTxnSelector: tSelectorFn = ({ isTaskConsumer }: tArgs) => {
  return !isTaskConsumer;
};

const requestPayoutSelector: tSelectorFn = ({ isTaskMaster, task }) => {
  return (
    isTaskMaster &&
    !task.personal &&
    task.safe &&
    (task.payout_status === "WAITING" || task.payout_status === "ERROR")
  );
};

export const useButtonsSelectors = ({
  task,
  now,
  isTaskMaster,
  isTaskConsumer,
}: tArgs) => {
  return useMemo(() => {
    const isStarted = moment(task.start_date).isBefore(now);
    const isEnded = moment(task.end_date).isBefore(now);
    const props = {
      task,
      now,
      isTaskMaster,
      isTaskConsumer,
      isStarted,
      isEnded,
    };
    const result = {
      requestPayout: requestPayoutSelector(props),
      message: messageSelector(props),
      cancel: cancelSelector(props),
      accept: acceptSelector(props),
      change: changeSelector(props),
      review: reviewSelector(props),
      pay: paySelector(props),
      finish: finishSelector(props),
      photoUpload: photoUploadSelector(props),
      photoRequest: photoRequestSelector(props),
      reviewRequest: reviewRequestSelector(props),
      dispute: disputeSelector(props),
      disputeUpdate: disputeUpdateSelector(props),
      disputeRemove: disputeRemoveSelector(props),
      remove: removeSelector(props),
      remind: remindSelector(props),
      copy: copyTaskSelector(props),
      createWarehouseTxn: createWarehouseTxnSelector(props),
    };
    return result;
  }, [task, now, isTaskMaster, isTaskConsumer]);
};
